// apps/frontend/src/hooks/useNextQuestionForMe.ts
// Hook user-facing pour Community Voice. Recupere au max 1 survey
// a presenter. Aucun mot "survey" cote nom externe — c'est juste
// "la prochaine question pour moi".
//
// Comportement :
//   - Defere 3s apres mount (pas d'embuscade au load)
//   - Poll toutes les 90s pour catcher un nouveau survey publie
//   - Respecte le guard critical path
//   - submit/dismiss invalident la prochaine query

import { useCallback, useEffect, useState } from 'react';
import apiClient from '../services/apiClient';
import { useSurveyDisplayGuard } from './useSurveyDisplayGuard';

export type QuestionType =
  | 'MOOD'
  | 'NPS'
  | 'RATING'
  | 'SINGLE_CHOICE'
  | 'MULTI_CHOICE'
  | 'TEXT';

export interface QuestionOption {
  value: string;
  label: string;
  icon?: string;
}

export interface PendingQuestion {
  id: string;
  position: number;
  type: QuestionType;
  topic: string;
  label: string;
  required: boolean;
  options: QuestionOption[] | null;
}

export interface PendingSurvey {
  id: string;
  title: string;
  intro: string | null;
  priority: number;
  questions: PendingQuestion[];
}

interface AnswerPayload {
  questionId: string;
  valueText?: string;
  valueRating?: number;
  valueChoice?: string | string[];
}

const INITIAL_DEFER_MS = 3_000;
const POLL_INTERVAL_MS = 90_000;

export function useNextQuestionForMe(enabled: boolean) {
  const canShow = useSurveyDisplayGuard();
  const [survey, setSurvey] = useState<PendingSurvey | null>(null);
  const [ready, setReady] = useState(false);

  const fetchPending = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await apiClient.request<{ survey: PendingSurvey | null }>(
        '/api/v1/surveys/pending'
      );
      if (res.success) {
        const raw = (res.data as { survey: PendingSurvey | null })?.survey;
        if (raw) {
          // Normaliser options array
          const normalized: PendingSurvey = {
            ...raw,
            questions: raw.questions.map(q => ({
              ...q,
              options: Array.isArray(q.options)
                ? (q.options as QuestionOption[])
                : null,
            })),
          };
          setSurvey(normalized);
        } else {
          setSurvey(null);
        }
      }
    } catch {
      // silencieux
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setSurvey(null);
      setReady(false);
      return;
    }
    const t = setTimeout(() => {
      setReady(true);
      fetchPending();
    }, INITIAL_DEFER_MS);
    const id = setInterval(fetchPending, POLL_INTERVAL_MS);
    return () => {
      clearTimeout(t);
      clearInterval(id);
    };
  }, [enabled, fetchPending]);

  const submitAnswers = useCallback(
    async (surveyId: string, answers: AnswerPayload[], skipped = false) => {
      try {
        await apiClient.request(`/api/v1/surveys/${surveyId}/answer`, {
          method: 'POST',
          body: JSON.stringify({ answers, skipped }),
        });
      } finally {
        setSurvey(null);
      }
    },
    []
  );

  const dismiss = useCallback(async (surveyId: string) => {
    try {
      await apiClient.request(`/api/v1/surveys/${surveyId}/dismiss`, {
        method: 'POST',
      });
    } finally {
      setSurvey(null);
    }
  }, []);

  const visible = ready && canShow && !!survey;

  return { survey, visible, submitAnswers, dismiss };
}
